import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DocumentRevisionCounts } from '../pages/Project/projectSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chart: {
      height: 280,
      [theme.breakpoints.down('sm')]: {
        height: 240
      },
      [theme.breakpoints.down('xs')]: {
        height: 200
      }
    }
  })
);

interface ChartCountDocumentProps {
  documentRevisionCounts: DocumentRevisionCounts[];
  startDate: moment.Moment;
  endDate: moment.Moment;
  planLabel?: string;
  actualLabel?: string;
  loading?: boolean;
}

export function formatCountDocument({
  startDate,
  endDate,
  documentRevisionCounts
}: ChartCountDocumentProps) {
  const start = moment(startDate);
  const end = moment(endDate);
  const results = [];

  while (start.isSameOrBefore(end, 'month')) {
    const foundData = documentRevisionCounts.find((documentRevisionCount) =>
      moment(documentRevisionCount.month).isSame(start, 'month')
    );
    results.push({
      month: start.format('MMM-YY'),
      actual: foundData ? foundData.actual : 0,
      plan: foundData ? foundData.plan : 0
    });

    start.add(1, 'months');
  }

  return results;
}

export default function ChartCountDocument({
  startDate,
  endDate,
  documentRevisionCounts,
  planLabel,
  actualLabel,
  loading = false
}: ChartCountDocumentProps) {
  const { t } = useTranslation();

  const defaultPlanLabel = planLabel || (t('chartCountDocument.planDocument') as string);
  const defaultActualLabel = actualLabel || (t('chartCountDocument.actualDocument') as string);
  const classes = useStyles();

  const countDocumentDates = formatCountDocument({
    startDate,
    endDate,
    documentRevisionCounts
  });

  const colors = countDocumentDates.map((countDocumentDate) =>
    countDocumentDate.actual < countDocumentDate.plan
      ? '#fa3c3cbb'
      : '#1877f2bb'
  );

  const options: ChartOptions<'bar'> = {
    maintainAspectRatio: false,
    responsive: true,
    elements: {
      bar: {
        borderWidth: 0
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      xAxes: {
        display: true,
        grid: {
          display: false
        }
      },
      yAxes: {
        display: true,
        ticks: { stepSize: 1 },
        beginAtZero: true
      }
    }
  };

  const data = {
    labels: countDocumentDates.map(
      (countDocumentData) => countDocumentData.month
    ),
    datasets: [
      {
        label: defaultPlanLabel,
        data: countDocumentDates.map(
          (countDocumentData) => countDocumentData.plan
        ),
        backgroundColor: '#ccccccbb',
        borderRadius: Number.MAX_VALUE
      },
      {
        label: defaultActualLabel,
        data: countDocumentDates.map(
          (countDocumentData) => countDocumentData.actual
        ),
        backgroundColor: colors,
        borderRadius: Number.MAX_VALUE
      }
    ]
  };

  return (
    <div className={classes.chart}>
      {loading ? (
        <Skeleton
          animation="wave"
          variant="rect"
          height={280}
          style={{ borderRadius: 8 }}
        />
      ) : (
        <Bar options={options} data={data} />
      )}
    </div>
  );
}
